import {
	pipe,
	pathOr,
	prop,
	filter,
	keys,
	map,
	join,
	startsWith,
	values,
	always,
} from 'ramda';

import {utils, writeFileXLSX} from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import {
	AlertTypeTitle,
	iTrip,
	iDevicePing,
	iList,
	iPerson,
	iReportTravelData,
	iReportStaticData,
	iFullTravelData,
	iFullStaticData,
	iSummaryItemResults,
	iExportReportPdfProps,
	iExportReportProps,
	DateTimeFormatOptions,
	iMileageReportData,
	iExtraInfo,
} from '../../../shared/interfaces';
import { csvExporter } from '../../../shared/csv';
import { DevicesDetailsContainer } from '../../../stores/reducers/devicesData';
import {
	friendlyDiff,
	minutesToFriendly,
	friendlySpeed,
	friendlyMilesPer,
	friendlyDist,
	countDateWithUserAndTimezoneOffset,
	exportPDF,
	calcTotalTime,
	timeClockRecords,
} from '../../../shared/helpers';
import moment from 'moment';
import autoTable, { UserOptions } from 'jspdf-autotable';
import {
	mapFromDeviceResultToTableBody,
	checkFileResolution,
	generateSummaryResults, mapFromDeviceResultToTableXlsx,
} from './SummaryReport/helper';
import {getPermissionsForUsers, formatAlertWithHas} from "./report-utils";
import {clientStore} from "../../../shared/firebase";
import {makePointFromEndpointData} from '../../../shared/db/report-db';
import jsPDF from "jspdf";
import {localStorage} from "../../../shared/storage";
import instance from "../../../api/instance";

interface iTravelTripData {
	id: string;
	personId?: string;
	label?: string;
	device?: string;
}

type AddressParts = {
	street: string;
	city: string;
	state: string;
	zip: string;
};

const distanceColLabel = (units?: string): string => {
	if (units === 'kilo') return 'Distance (km)';
	if (units === 'knots') return 'Distance (knots)';
	return 'Distance (miles)';
};

const numericDistValue = (miles: number, units?: string): number => {
	const str = friendlyDist(miles, units as any);
	const match = str.match(/\d+/);
	return match ? parseInt(match[0], 10) : 0;
};

const parseTripAddress = (raw?: string): AddressParts => {
	if (!raw) return { street: '', city: '', state: '', zip: '' };

	let s = String(raw)
		.replace(/\S*\+\S*/g, '')
		.replace(/\s+/g, ' ')
		.trim();

	// Special case: handle addresses like '66 Hunter Ln CT 06082, US Enfield'
	let usMatch = s.match(/^(.*?)(?:,\s*US\s*)(\w+)$/i);
	if (usMatch) {
		const leftPart = usMatch[1].trim();
		const cityPart = usMatch[2].trim();
		// Try to extract state and zip from leftPart
		const m = leftPart.match(/(.*)\s([A-Z]{2})\s(\d{5}(?:-\d{4})?)$/);
		if (m) {
			return {
				street: m[1].trim(),
				state: m[2],
				zip: m[3],
				city: cityPart
			};
		}
	}

	const unSplit = s.split(/,\s*UN\s+/i);
	let left = (unSplit[0] ?? '').trim();
	let city = (unSplit[1] ?? '').trim();

	if (!city) {
		const parts = left
			.split(',')
			.map(p => p.trim())
			.filter(Boolean);

		if (parts.length >= 3) {
			city = parts[parts.length - 2];
			left = `${parts.slice(0, parts.length - 2).join(', ')}, ${parts[parts.length - 1]}`.trim();
		}
	}

	let state = '';
	let zip = '';

	const m = left.match(/\b([A-Z]{2})\s*(\d{5}(?:-\d{4})?)\b/);
	if (m) {
		state = m[1];
		zip = m[2];
		left = left.slice(0, m.index).trim();
	}

	const street = left.replace(/,+\s*$/, '').trim();

	// Enhanced city extraction: if city is still empty, try to extract from street
	if (!city && street) {
		const streetSuffixes = [
			'st', 'rd', 'ave', 'blvd', 'ln', 'dr', 'ct', 'trl', 'pkwy', 'cir', 'way', 'pl', 'ter', 'loop', 'hwy', 'sq', 'cv', 'walk', 'run', 'pass', 'row', 'path', 'cres', 'bnd', 'pt', 'grv', 'mnr', 'byp', 'xrd', 'aly', 'ext', 'pky', 'crk', 'mt', 'est', 'estates', 'park', 'crossing', 'commons', 'ridge', 'heights', 'view', 'meadow', 'woods', 'grove', 'valley', 'hill', 'lake', 'falls', 'harbor', 'bay', 'beach', 'cove', 'point', 'isle', 'island', 'landing', 'manor', 'mill', 'orchard', 'place', 'plaza', 'spring', 'village', 'vista', 'wells', 'garden', 'gardens', 'field', 'fields', 'brook', 'bridge', 'fork', 'forks', 'haven', 'hollow', 'knoll', 'landing', 'light', 'meadows', 'pines', 'plain', 'plains', 'prairie', 'rapids', 'rest', 'ridge', 'river', 'shore', 'shores', 'spring', 'springs', 'station', 'stream', 'summit', 'trace', 'turn', 'vistas', 'walk', 'wall', 'way', 'well', 'wells', 'wood', 'woods'
		];
		const streetWords = street.split(' ').filter(Boolean);
		if (streetWords.length > 1) {
			const lastWord = streetWords[streetWords.length - 1].replace(/\.|,/g, '').toLowerCase();
			if (!streetSuffixes.includes(lastWord)) {
				city = streetWords[streetWords.length - 1];
				// Remove city from street
				streetWords.pop();
				left = streetWords.join(' ');
			}
		}
	}
	return { street: left || street, city, state, zip };
};

// we can't really use device units preference
// because we are combining devices.
const _tripRecordToRow =
	(devicesDetails: DevicesDetailsContainer, people: iList<iPerson>) => (record: iTrip) => {
		const timezone = devicesDetails.getIn([record.device, 'timezone']) as string;
		const start = countDateWithUserAndTimezoneOffset(record.startDate, timezone);
		const end = countDateWithUserAndTimezoneOffset(record.endDate, timezone);
		return {
			'Device Name': (devicesDetails.getIn([record.device, 'name']) as string) || 'device not found',
			'Trip Minutes': record.endDate.diff(record.startDate, 'minutes'),
			'Person': people[record.personId] ? people[record.personId].displayName : 'N/A',
			'Label': record.label || 'N/A',
			'Start Time': start.format('M/D/YY h:mm A'),
			'Start Address': record.startAddress,
			'End Time': end.format('M/D/YY h:mm A'),
			'End Address': record.endAddress,
			'Idle Minutes': record.idleMinutes,
			'Stopped Minutes': record.stopMinutes.toFixed(1),
			'Max Speed': record.maxSpeed,
			'Average Speed': record.averageSpeed.toFixed(1),
			'Miles Traveled': record.miles,
			MPG: record.mpg,
			'Safety Percent': `${record.safetyPercent}%`,
			// Alerts: pipe(
			// 	prop('alertActivity'),
			// 	filter(Boolean),
			// 	keys,
			// 	filter(startsWith('has')),
			// 	map(AlertTypeTitle),
			// 	join(', ')
			// )(record),
		};
	};

const _timeClockRecordToRow =
	(devicesDetails: DevicesDetailsContainer, people: iList<iPerson>) => (record: iTrip) => {
		return {
			'Device Name': (devicesDetails.getIn([record.device, 'name']) as string) || 'device not found',
			'Start Date': record.startDate.format('MM/DD/YYYY'),
			'Start Time': record.startDate.format('h:mmA'),
			'End Time': record.endDate.format('h:mmA'),
			'Total time': calcTotalTime(record.endDate, record.startDate),
		};
	};

const _pointToRow =
	(devicesDetails: DevicesDetailsContainer, people: iList<iPerson>) => (record: iDevicePing) => {
		const timezone = devicesDetails.getIn([record.device, 'timezone']) as string;
		const dateWithCorrection = countDateWithUserAndTimezoneOffset(record.time, timezone);
		return {
			Person: pathOr('Not assigned', [record.personId, 'displayName'], people),
			Device: (devicesDetails.getIn([record.device, 'name']) as string) || '',
			Time: dateWithCorrection.format('M/D/YY h:mm A'),
			Street: record.address.street,
			City: record.address.city,
			State: record.address.state,
			Zip: record.address.zip,
			Latitude: record.coordinates.location.lat,
			Longitude: record.coordinates.location.lng,
			Label: record.label,
			Fuel: record.fuel,
			MPG: record.mpg,
			Miles: record.miles,
			Message: record.msg,
			'Posted Speed': record.posted_speed,
			MPH: record.speed,
			Voltage: record.volt,
		};
	};

export const exportReport = ({
	displayRecords,
	reportType,
	devicesDetails,
	people,
}: iExportReportProps) => {
	console.log(displayRecords)

	return pipe(
		always(displayRecords),
		values,
		map(
			reportType === 'travel'
				? _tripRecordToRow(devicesDetails, people)
				: _pointToRow(devicesDetails, people)
		),
		// turn into header row + values of rest
		records => [keys(records[0]), ...records.map(values)],
		csvExporter('report.csv')
	) as () => any;
}

const getReportTravelData = ({ group, device, people }: iReportTravelData): UserOptions[] => {
	const { timezone, name, units } = device;
	return Object.values(group).map(row => {
		const start = countDateWithUserAndTimezoneOffset(row.startDate, timezone);
		const end = countDateWithUserAndTimezoneOffset(row.endDate, timezone);
		const personKey = row.personId;
		const person = people[personKey] || { displayName: '' };
		return {
			head: [[name, start.format('M/D/YY'), '', `${start.format('h:mmA')} - ${row.startAddress}`]],
			body: [
				['', 'Duration:', friendlyDiff(moment(row.endDate), moment(row.startDate))],
				['', 'Idle:', minutesToFriendly(row.idleMinutes)],
				['', 'Stopped:', minutesToFriendly(row.stopMinutes)],
				['', 'Safety:', row.safetyPercent ? row.safetyPercent + '%' : 'N/A'],
				['', 'Person', person.displayName],
				['', 'Avg. Speed:', `${row.averageSpeed ? friendlySpeed(row.averageSpeed, units) : 'N/A'}`],
				['', 'Max Speed', `${row.maxSpeed ? friendlySpeed(row.maxSpeed, units) : 'N/A'}`],
				['', 'Distance', friendlyDist(row.miles, units)],
				['', 'Consumption:', friendlyMilesPer(row.mpg, units)],
				['', 'Label', row.label || ''],
			],
			foot: [['', '', '', `${end.format('h:mmA')} - ${row.endAddress}`]],
		};
	});
};

const getReportTravelDataXlsx = ({ group, device, people }: iReportTravelData) => {
	const { timezone, name, units } = device;
	return Object.values(group).map(row => {
		const start = countDateWithUserAndTimezoneOffset(row.startDate, timezone);
		const end = countDateWithUserAndTimezoneOffset(row.endDate, timezone);
		const personKey = row.personId;
		const person = people[personKey] || { displayName: '' };
		return {
			deviceName: name,
			duration: friendlyDiff(moment(row.endDate), moment(row.startDate)),
			label: row.label || 'N/A',
			person: person.displayName || 'N/A',
			startTime: start.format('M/D/YY') + ' ' + start.format('h:mmA'),
			startAddress: row.startAddress,
			endTime: end.format('M/D/YY') + ' ' + end.format('h:mmA'),
			endAddress: row.endAddress,
			idleMinutes: minutesToFriendly(row.idleMinutes),
			stoppedMinutes: minutesToFriendly(row.stopMinutes),
			maxSpeed: row.maxSpeed ? friendlySpeed(row.maxSpeed, units) : 'N/A',
			averageSpeed: row.averageSpeed ? friendlySpeed(row.averageSpeed, units) : 'N/A',
			milesTraveled: friendlyDist(row.miles, units),
			mpg: friendlyMilesPer(row.mpg, units),
			safetyPercent: row.safetyPercent ? row.safetyPercent + '%' : 'N/A'
		};
	});
};

const formattedTimeCLockData = (records): iDevicePing[] => {
	const groupedData = {};
	timeClockRecords(records).map(record => {
		const date = record.startDate.format("MM/DD/YYYY");
		const objectKey = record.device + date;
		if (!groupedData[objectKey]) {
			//if no such deviceName exists in groupedData,
			// then we add it there and set the initial values
			groupedData[objectKey] = {
				...record,
			};
		} else {
			// if deviceName is already in groupedData,
			// then compare the dates and update the values if necessary
			if (record.startDate < groupedData[objectKey].startDate) {
				groupedData[objectKey].startDate = record.startDate;
			}
			if (record.endDate > groupedData[objectKey].endDate) {
				groupedData[objectKey].endDate = record.endDate;
			}
		}
	});
	return  Object.values(groupedData);
}

const getReportTimeClockData = ({ group, device, people }: iReportTravelData, fileType): UserOptions[] => {
	const { name } = device;
	const formattedData = formattedTimeCLockData(Object.values(group));
	return formattedData.map(row => {

	if (fileType === 'pdf') {
		return {
			head: [['Device name', name]],
			body: [
				['Start date', row.startDate.format('MM/DD/YYYY')],
				['Start time', row.startDate.format('h:mmA')],
				['End time', row.endDate.format('h:mmA')],
			],
			foot: [['Total', calcTotalTime(row.endDate, row.startDate)]],
		};
	} else if (fileType === 'xlsx') {
		return {
			deviceName: name,
			startDate: row.startDate.format('MM/DD/YYYY'),
			startTime: row.startDate.format('h:mmA'),
			endTime: row.endDate.format('h:mmA'),
			total: calcTotalTime(row.endDate, row.startDate),
		}
	}
	});
};

const getReportStaticData = ({ group, device, people }: iReportStaticData): UserOptions[] => {
	const { timezone, name } = device;
	const bodyData = Object.values(group).map(row => {
		const dateWithCorrection = countDateWithUserAndTimezoneOffset(row.time, timezone);
		const personKey = row.personId;
		const name = people[personKey] ? people[personKey].displayName : 'N/A';

		return [
			dateWithCorrection.format('MMM-D'),
			dateWithCorrection.format('h:mmA'),
			name,
			row.label || 'N/A',
			row.fuel,
			row.volt,
			row.speed,
			row.posted_speed,
			row.mpg,
			row.msg,
			`${row.address.street} ${row.address.city} ${row.address.state} ${row.address.zip}`.replace(/US/gi, '').replace(/\S*\+\S*/g, ''),
			`${row.coordinates.location['lat']}, ${row.coordinates.location['lng']}`,
		];
	});

	return [
		{
			theme: 'grid',
			headStyles: { fillColor: '#2E80BA' },
			head: [[name, 'Data', 'Person', 'Label', 'Fuel', 'Voltage', 'mph', 'Posted', 'mpg', 'Message', 'Location', 'Coordinates']],
			body: bodyData,
		},
	];
};

const getReportStaticDataXlsx = ({ group, device, people }: iReportStaticData) => {
	const { timezone, name } = device;
	return Object.values(group).map(row => {
		const dateWithCorrection = countDateWithUserAndTimezoneOffset(row.time, timezone);
		const personKey = row.personId;
		const personName = people[personKey] ? people[personKey].displayName : 'N/A';

		return {
			name,
			date: dateWithCorrection.format('MMM-D') + " " + dateWithCorrection.format('h:mmA'),
			person: personName,
			label: row.label || 'N/A',
			fuel: row.fuel,
			voltage: row.volt,
			mph: row.speed,
			posted: row.posted_speed,
			mpg: row.mpg,
			message: row.msg,
			location: `${row.address.street} ${row.address.city} ${row.address.state} ${row.address.zip}`.replace(/US/gi, '').replace(/\S*\+\S*/g, ''),
			coordinates: `${row.coordinates.location['lat']}, ${row.coordinates.location['lng']}`,
		};
	});
};

const getFullTravelData = ({ records, reportData, people }: iFullTravelData) => {
	const fullTravelData = [];
	Object.entries(records).forEach(([deviceId, group], index) => {
		const device = reportData.get(deviceId);
		if (!device) return;
		const travelTableData = getReportTravelData({ group, device, people });

		// travelTableData is an array of UserOptions
		// we need to save an one array instead array of UserOptions arrays
		fullTravelData.push(...travelTableData);
	});

	return fullTravelData;
};

const getTravelDataXlsx = ({ records, reportData, people }: iFullTravelData) => {
	const fullTravelData = [];
	Object.entries(records).forEach(([deviceId, group], index) => {
		const device = reportData.get(deviceId);
		if (!device) return;
		const travelTableData = getReportTravelDataXlsx({ group, device, people });

		// travelTableData is an array of UserOptions
		// we need to save an one array instead array of UserOptions arrays
		fullTravelData.push(...travelTableData);
	});

	return fullTravelData;
};

const getFullStaticData = ({ records, reportData, people }: iFullStaticData) => {
	const fullStaticData = [];
	Object.entries(records).forEach(([deviceId, group], index) => {
		const device = reportData.get(deviceId);
		if (!device) return;
		const staticTableData = getReportStaticData({ group, device, people });

		// travelTableData is an array of UserOptions
		// we need to save an one array instead array of UserOptions arrays
		fullStaticData.push(...staticTableData);
	});

	return fullStaticData;
};

const getFullStaticDataXlsx = ({ records, reportData, people }: iFullStaticData) => {
	const fullStaticData = [];
	Object.entries(records).forEach(([deviceId, group], index) => {
		const device = reportData.get(deviceId);
		if (!device) return;
		const staticTableData = getReportStaticDataXlsx({ group, device, people });

		// travelTableData is an array of UserOptions
		// we need to save an one array instead array of UserOptions arrays
		fullStaticData.push(...staticTableData);
	});

	return fullStaticData;
};

export const exportTimeClockReportCsv = ({
   displayRecords,
   devicesDetails,
   people,
}) => {
	return pipe(
		always(formattedTimeCLockData(Object.values(displayRecords))),
		values,
		map(_timeClockRecordToRow(devicesDetails, people)),
		// turn into header row + values of rest
		records => [keys(records[0]), ...records.map(values)],
		csvExporter('report_time-clock.csv')
	) as () => any
};


export const exportTimeClockReportPdf = ({
  records,
  reportData,
  people,
}) => {
	const allTables = getFullTimeClockData({ records, reportData, people }, 'pdf');

	exportPDF({
		fileName: 'Report_time-clock',
		data: allTables,
	});
};

export const exportTimeClockReportXlsx = ({
  records,
  reportData,
  people,
}) => {
	const allTables = getFullTimeClockData({ records, reportData, people }, 'xlsx');

	const worksheet = utils.json_to_sheet(allTables);
	// column width in file
	worksheet['!cols'] = [{wch: 20}, {wch: 15}, {wch: 8}, {wch: 8}, {wch: 8}];
	const workbook = utils.book_new();
	utils.book_append_sheet(workbook, worksheet, "report_time-clock");
	writeFileXLSX(workbook, "Report_time-clock.xlsx");
};

const getFullTimeClockData = ({ records, reportData, people }: iFullTravelData, fileType) => {
	const fullTravelData = [];
	Object.entries(records).forEach(([deviceId, group], index) => {
		const device = reportData.get(deviceId);
		if (!device) return;
		const travelTableData = getReportTimeClockData({ group, device, people }, fileType);

		fullTravelData.push(...travelTableData);
	});
	return fullTravelData;
};

export const exportReportPdf: (params: iExportReportPdfProps) => void = ({
	records,
	reportData,
	report,
	people,
}) => {
	console.log(records)
	const allTables =
		report.reportType === 'travel'
			? getFullTravelData({ records, reportData, people })
			: getFullStaticData({ records, reportData, people }); // report.reportType === 'static'

	exportPDF({
		fileName: 'Report',
		data: allTables,
	});
};

export const exportReportXlsx: (params: iExportReportPdfProps) => void = ({
  records,
  reportData,
  report,
  people,
}) => {
	const allTables =
		report.reportType === 'travel'
			? getTravelDataXlsx({ records, reportData, people })
			: getFullStaticDataXlsx({ records, reportData, people }); // report.reportType === 'static'
	const worksheet = utils.json_to_sheet(allTables);
	const workbook = utils.book_new();
	utils.book_append_sheet(workbook, worksheet, "report");
	writeFileXLSX(workbook, "Report.xlsx");
};

export const exportSummary = (data, deviceUnits, devicesDetails, reportDates, exportType) => {
	const results = {};

	Object.values(data).forEach((deviceTrips: any) => {
		const deviceName = devicesDetails.get(deviceTrips[0].device)?.name || 'Unknown Device';
		results[deviceTrips[0].device] = generateSummaryResults(deviceTrips, deviceName, 'summary');
	});

	const outputPDF = Object.values(results).map((device: iSummaryItemResults) => {
		return {
			head: [[device.device, '']],
			body: mapFromDeviceResultToTableBody(device, deviceUnits),
		};
	});

	const outputXLSX = Object.values(results).map((device: iSummaryItemResults) => mapFromDeviceResultToTableXlsx(device, deviceUnits))

	const outputCSV = [
		['Device', 'Average Speed', 'Safety', 'Drive Time', 'Stopped Time', 'Total Distance'],
	];
	Object.values(results).forEach(
		(device: iSummaryItemResults, index: number, array: iSummaryItemResults[]) => {
			const deviceInfo = [...mapFromDeviceResultToTableBody(device, deviceUnits, true)];
			outputCSV.push(...deviceInfo);
		}
	);

	const dateOptions: DateTimeFormatOptions = {
		day: '2-digit',
		month: '2-digit'
	}
	const fileName = reportDates.length > 1 ? reportDates.map(date => new Intl.DateTimeFormat('en-US', dateOptions).format(date.startDate)).join(', ') : `${new Intl.DateTimeFormat('en-US', dateOptions).format(reportDates[0].startDate)} - ${new Intl.DateTimeFormat('en-US', dateOptions).format(reportDates[0].endDate)}`;

	if (exportType === 'csv') {
		const newFileName = checkFileResolution({ typeWithoutDot: 'csv', fileName });
		csvExporter(newFileName)(outputCSV);
	} else if (exportType === 'pdf') {
		const newFileName = checkFileResolution({ typeWithoutDot: 'pdf', fileName });
		exportPDF({
			fileName: newFileName,
			data: outputPDF,
		});
	} else if (exportType === 'xlsx') {
		const worksheet = utils.json_to_sheet(outputXLSX);
		const workbook = utils.book_new();
		utils.book_append_sheet(workbook, worksheet, "summary");
		writeFileXLSX(workbook, "ReportSummary.xlsx");
	}
};

const getCsvAndXlsxRecords = async (persons) => {
	const records = await getPermissionsForUsers(persons);
	const exportData = [];

	for (const record of records) {
		const displayName = record.displayName;

		for (const device of record.devices) {
			const deviceName = device.name || '';
			const permissionLevel = device.readOnly ? 'Read Only' : 'Full';
			let assignmentType = '';

			if (device.tag) assignmentType += `Tag: ${device.tag}\n`;
			if (device.device) assignmentType += `Direct assignment`;

			exportData.push({
				displayName,
				deviceName,
				assignmentType,
				permissionLevel
			})
		}
	}

	return exportData;
}

export const exportPermissionsPdf = async (persons) => {
	const records = await getPermissionsForUsers(persons);

	const exportData = [];

	for (const record of records) {
		const data = {};

		data['head'] = [[record.displayName, 'Device', 'Assignment Type', 'Permission Level']];
		data['body'] = [];

		for (const device of record.devices) {
			const deviceName = device.name || '';
			const permissionLevel = device.readOnly ? 'Read Only' : 'Full';
			let assignmentType = '';

			if (device.tag) assignmentType += `Tag: ${device.tag}\n`;
			if (device.device) assignmentType += `Direct assignment`;

			data['body'].push(['', deviceName, assignmentType, permissionLevel]);
		}

		exportData.push(data);
	}

	exportPDF({
		fileName: 'Report',
		data: exportData
	})
};

export const exportPermissionsXlsx = async (persons) => {
	const exportData = await getCsvAndXlsxRecords(persons);

	const worksheet = utils.json_to_sheet(exportData);
	const workbook = utils.book_new();

	utils.book_append_sheet(workbook, worksheet, "report");
	writeFileXLSX(workbook, "Report.xlsx");
};

export const exportPermissionsCsv = async (persons) => {
	const exportData = await getCsvAndXlsxRecords(persons);

	csvExporter('Report.csv')([['User name', 'Device', 'Assignment type', 'Permission'], ...exportData.map(data => {
		return [data.displayName, data.deviceName, data.assignmentType, data.permissionLevel]
	})])
};

const getTripByIds = async (ids, filters, devices) => {
	const peopleFilter = filters.person ? Object.keys(filters.person) : [];
	const labelsFilter = filters.labels ? Object.keys(filters.labels) : [];
	const chunkSize = 10;

	const chunks = [];
	for (let i = 0; i < ids.length; i += chunkSize) {
		chunks.push(ids.slice(i, i + chunkSize));
	}

	const promises = chunks.map(async chunk => {
		let query = await clientStore().collection('trips')
			.where('__name__', 'in', chunk)
			.get();

		return query.docs.map(doc => (({
            id: doc.id,
            ...doc.data()
        }) as iTravelTripData));
	});

	const results = (await Promise.all(promises)).flat();

	const filteredResults = results.filter(doc => {
		const hasPersonId = doc.personId && peopleFilter.includes(doc.personId);
		const hasLabel = doc.label && labelsFilter.includes(doc.label);

		if (peopleFilter.length === 0 && labelsFilter.length === 0) {
			return true;
		}

		return (peopleFilter.length && hasPersonId) || (labelsFilter.length && hasLabel);
	});

	const deviceGroups = filteredResults.reduce((groups, trip) => {
		const device = trip.device || '';

		if (!groups[device]) {
			groups[device] = [];
		}
		groups[device].push(trip);

		return groups;
	}, {});

	const groupsMappedEntries = Object.entries(deviceGroups).map(([deviceId, data]) => {
		const device = devices.getIn([deviceId]) as any;
		const displayName = device ? device.name : '';

		return [
			deviceId,
			displayName,
			data
		];
	})

	groupsMappedEntries.sort((a, b) => (a[1] as string).localeCompare(b[1] as string));

	const sortedByDeviceGroups = {};
	for (const [deviceId, , data] of groupsMappedEntries) {
		sortedByDeviceGroups[deviceId] = data;
	}

	const sortedGroups = Object.entries(sortedByDeviceGroups).map(([device, deviceTrips]) => {
		return (deviceTrips as iTrip[]).sort((a, b) => {
			const aTime = (a.startDate.seconds ?? 0) as number;
			const bTime = (b.startDate.seconds ?? 0) as number;

			return aTime - bTime;
		});
	});

	return sortedGroups.flat();
}

const getFullTravelReportData = ({reportData, people}, records) => {
	const data = [];

	for (const record of records) {
		const device = reportData.get(record.device);

		if (!device) continue;

		const person = record.personId ? people[record.personId] : { displayName: ''};
		const startDate = moment(record.startDate.seconds * 1000);
		const endDate = moment(record.endDate.seconds * 1000);
		const start = countDateWithUserAndTimezoneOffset(startDate, device.timezone);
		const end = countDateWithUserAndTimezoneOffset(endDate, device.timezone);

		const report = {
			head: [[device.name, start.format('M/D/YY'), '', `${start.format('h:mmA')} - ${record.startAddress}`]],
			body: [
				['', 'Duration:', friendlyDiff(endDate, startDate)],
				['', 'Idle:', minutesToFriendly(record.idleMinutes)],
				['', 'Stopped:', minutesToFriendly(record.stopMinutes)],
				['', 'Safety:', record.safetyPercent ? record.safetyPercent + '%' : 'N/A'],
				['', 'Person', person.displayName],
				['', 'Avg. Speed:', `${record.averageSpeed ? friendlySpeed(record.averageSpeed, device.units) : 'N/A'}`],
				['', 'Max Speed', `${record.maxSpeed ? friendlySpeed(record.maxSpeed, device.units) : 'N/A'}`],
				['', 'Distance', friendlyDist(record.miles, device.units)],
				['', 'Consumption:', friendlyMilesPer(record.mpg, device.units)],
				['', 'Label', record.label || ''],
			],
			foot: [['', '', '', `${end.format('h:mmA')} - ${record.endAddress}`]],
		}

		data.push(report);
	}

	return data;
}

const getFullTravelReportDataXlsx = ({reportData, people, allExtraInfo = {}}, records) => {
	const data = [];

	// Build dynamic extra info columns (same logic as inventory report)
	const extraInfoColumns = Object.values(allExtraInfo as iList<iExtraInfo>)
		.filter(item => !item.details.isHidden)
		.map(item => ({
			id: item.details.id,
			label: item.details.name,
			dateLabel: `${item.details.name} Date`
		}))
		.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));

	for (const record of records) {
		const device = reportData.get(record.device);

		if (!device) continue;

		const person = record.personId ? people[record.personId] : { displayName: ''};
		const startDate = moment(record.startDate.seconds * 1000);
		const endDate = moment(record.endDate.seconds * 1000);
		const start = countDateWithUserAndTimezoneOffset(startDate, device.timezone);
		const end = countDateWithUserAndTimezoneOffset(endDate, device.timezone);

		const startParts = parseTripAddress(record.startAddress);
		const endParts = parseTripAddress(record.endAddress);

		// Extract all extra info fields from device
		const deviceExtraInfo = (device as any)['extra-info'] || (device as any).extraInfo || {};
		const extraInfoValues = {};
		extraInfoColumns.forEach(({ id, label, dateLabel }) => {
			const val = deviceExtraInfo[id];
			extraInfoValues[dateLabel] = '';
			if (!val) {
				extraInfoValues[label] = '';
			} else if (typeof val === 'object' && val !== null) {
				if (val.text) extraInfoValues[label] = val.text;
				else if (val.boolean !== undefined) extraInfoValues[label] = val.boolean ? 'Yes' : 'No';
				else extraInfoValues[label] = '';

				if (val.date) extraInfoValues[dateLabel] = val.date;
			} else {
				extraInfoValues[label] = String(val);
			}
		});

		const report = {
			'Vehicle': device.name,
			'Duration': friendlyDiff(endDate, startDate),
			'Start Date': start.format('M/D/YY'),
			'Start Time': start.format('h:mm'),
			'Start AM/PM': start.format('A'),

			'Start - Street': startParts.street,
			'Start - City': startParts.city,
			'Start - State': startParts.state,
			'Start - Zip': startParts.zip,

			'End Date': end.format('M/D/YY'),
			'End Time': end.format('h:mm'),
			'End AM/PM': end.format('A'),

			'End - Street': endParts.street,
			'End - City': endParts.city,
			'End - State': endParts.state,
			'End - Zip': endParts.zip,

			'Idle (min)': minutesToFriendly(record.idleMinutes),
			'Stopped (min)': minutesToFriendly(record.stopMinutes),
			'Max Speed': record.maxSpeed ? friendlySpeed(record.maxSpeed, device.units) : 'N/A',
			'Avg Speed': record.averageSpeed ? friendlySpeed(record.averageSpeed, device.units) : 'N/A',
			[distanceColLabel(device.units)]: numericDistValue(record.miles, device.units),
			'MPG': friendlyMilesPer(record.mpg, device.units),
			'Safety %': record.safetyPercent ? record.safetyPercent + '%' : 'N/A',
			...extraInfoValues
		};

		data.push(report);
	}

	return data;
}

const getFullTravelReportDataCsv = ({reportData, people, allExtraInfo = {}}, records) => {
	const data = [];

	// Build dynamic extra info columns (same logic as inventory report)
	const extraInfoColumns = Object.values(allExtraInfo as iList<iExtraInfo>)
		.filter(item => !item.details.isHidden)
		.map(item => ({
			id: item.details.id,
			label: item.details.name
		}))
		.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));

	for (const record of records) {
		const device = reportData.get(record.device);

		if (!device) continue;

		const person = record.personId ? people[record.personId] : { displayName: ''};
		const startDate = moment(record.startDate.seconds * 1000);
		const endDate = moment(record.endDate.seconds * 1000);
		const start = countDateWithUserAndTimezoneOffset(startDate, device.timezone);
		const end = countDateWithUserAndTimezoneOffset(endDate, device.timezone);

		const startParts = parseTripAddress(record.startAddress);
		const endParts = parseTripAddress(record.endAddress);

		// Extract all extra info fields from device
		const deviceExtraInfo = (device as any)['extra-info'] || (device as any).extraInfo || {};
		const extraInfoValues = {};
		extraInfoColumns.forEach(({ id, label }) => {
			const val = deviceExtraInfo[id];
			if (!val) {
				extraInfoValues[label] = '';
			} else if (typeof val === 'object' && val !== null) {
				if (val.text) extraInfoValues[label] = val.text;
				else if (val.date) extraInfoValues[label] = val.date;
				else if (val.boolean !== undefined) extraInfoValues[label] = val.boolean ? 'Yes' : 'No';
				else extraInfoValues[label] = '';
			} else {
				extraInfoValues[label] = String(val);
			}
		});

		const report = {
			'Vehicle': device.name,
			'Duration': friendlyDiff(endDate, startDate),
			'Start Date': start.format('M/D/YY'),
			'Start Time': start.format('h:mm'),
			'Start AM/PM': start.format('A'),

			'Start - Street': startParts.street,
			'Start - City': startParts.city,
			'Start - State': startParts.state,
			'Start - Zip': startParts.zip,

			'End Date': end.format('M/D/YY'),
			'End Time': end.format('h:mm'),
			'End AM/PM': end.format('A'),

			'End - Street': endParts.street,
			'End - City': endParts.city,
			'End - State': endParts.state,
			'End - Zip': endParts.zip,

			'Idle (min)': minutesToFriendly(record.idleMinutes),
			'Stopped (min)': minutesToFriendly(record.stopMinutes),
			'Max Speed': record.maxSpeed ? friendlySpeed(record.maxSpeed, device.units) : 'N/A',
			'Avg Speed': record.averageSpeed ? friendlySpeed(record.averageSpeed, device.units) : 'N/A',
			[distanceColLabel(device.units)]: numericDistValue(record.miles, device.units),
			'MPG': friendlyMilesPer(record.mpg, device.units),
			'Safety %': record.safetyPercent ? record.safetyPercent + '%' : 'N/A',
			...extraInfoValues
		};

		data.push(report);
	}

	return data;
}

const cleanNumericValuesForTravelType = (data) => {
  return data.map(row => {
    const newRow = { ...row };

	// camelCase for xlsx file and other for csv
    ['maxSpeed', 'averageSpeed', 'milesTraveled', 'mpg', 'Max Speed', 'Average Speed', 'Miles Traveled', 'MPG'].forEach(field => {
      if (typeof newRow[field] === 'string') {
        const match = newRow[field].match(/[\d.]+/);
        newRow[field] = match ? parseFloat(match[0]) : null;
      }
    });

    return newRow;
  });
};

const cleanNumericValuesForTravelTypePdf = (data) => {
  const targetFields = ['Max Speed', 'Average Speed', 'Miles Traveled', 'MPG'];

  return data.map((report) => {
    const newBody = report.body.map((row) => {
      const label = row[1];
      if (targetFields.includes(label)) {
        const match = row[2].match(/[\d.]+/);
        row[2] = match ? parseFloat(match[0]) : null;
      }

      return row;
    });

    return {
      ...report,
      body: newBody,
    };
  });
};


export const exportTravelReport = async ({reportData, report, people, travelTripIds, formats, allExtraInfo = {}}) => {
	const records = await getTripByIds(travelTripIds, report.filters, reportData);

	if (formats.exportPdf) {
		let data = await getFullTravelReportData({reportData, people}, records);
		data = cleanNumericValuesForTravelTypePdf(data);

		exportPDF({
			fileName: 'Report',
			data
		})
	}

	if (formats.exportXlsx) {
		let data = await getFullTravelReportDataXlsx({reportData, people, allExtraInfo}, records);
		data = cleanNumericValuesForTravelType(data);

		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet('report');

		if (data.length > 0) {
			const headers = Object.keys(data[0]);
			worksheet.columns = headers.map(h => ({ header: h, key: h }));
			worksheet.addRows(data);
			worksheet.eachRow((row, rowNumber) => {
				row.eachCell({ includeEmpty: true }, cell => {
					cell.font = rowNumber === 1
						? { name: 'Calibri', size: 9, bold: true }
						: { name: 'Calibri', size: 9 };
				});
			});
		}

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		saveAs(blob, 'Report.xlsx');
	}

	if (formats.exportCsv) {
		let data = await getFullTravelReportDataCsv({reportData, people, allExtraInfo}, records);
		data = cleanNumericValuesForTravelType(data);

		csvExporter('Report.csv')([Object.keys(data[0]), ...data.map(item => Object.values(item))]);
	}
}

const getAllStaticReportRecords = async (reportDetails, devicesDetails) => {
	const filters = reportDetails.filters || {};
	const clientId = localStorage.get('active-client');
	const URL = '/api/firestore/points-with-filter';

	const startTime = moment(reportDetails.dates?.[0]?.startDate ?? Date.now()).unix();
	const endTime = moment(reportDetails.dates?.[0]?.endDate ?? Date.now()).unix();

	const allRecords: Record<string, any> = {};

	const baseBody = {
		address: {
			...(filters.values?.city && { city: filters.values.city }),
			...(filters.values?.state && { state: filters.values.state }),
			...(filters.values?.street && { street: filters.values.street }),
			...(filters.values?.zip && { zip: filters.values.zip }),
		},
		metric: {
			...(filters.values?.fuel && { fuel: filters.values.fuel }),
			...(filters.values?.mpg && { mpg: filters.values.mpg }),
			...(filters.values?.rpm && { rpm: filters.values.rpm }),
			...(filters.values?.speed && { speed: filters.values.speed }),
			...(filters.values?.volts && { volts: filters.values.volts }),
		},
		...(filters.device && { device: Object.keys(filters.device) }),
		...(filters.person && { people: Object.keys(filters.person) }),
		...(filters.tag && { tag: Object.keys(filters.tag) }),
		...(filters.alerts && { alerts: Object.keys(filters.alerts).map(alert => formatAlertWithHas(alert)) }),
		...(filters.labels && { label: Object.keys(filters.labels) }),
		startDate: startTime,
		endDate: endTime,
		clientId,
	};

	let page = 1;
	let previousPageSignature = '';
	const maxPages = 1000;

	while (page <= maxPages) {
		try {
			const { data } = await instance.post(URL, { ...baseBody, page });
			const pageContent = Object.values(data?.content || {}) as any[];

			if (!pageContent.length) break;

			const pageSignature = pageContent.map(point => point?.pointId).filter(Boolean).join('|');
			if (pageSignature && pageSignature === previousPageSignature) break;
			previousPageSignature = pageSignature;

			for (const point of pageContent) {
				try {
					allRecords[point.pointId] = makePointFromEndpointData(point);
				} catch (e) {
					// skip malformed points
				}
			}

			page++;
		} catch (e) {
			console.error('Failed to fetch static report records for export:', e);
			break;
		}
	}

	return allRecords;
};

export const exportStaticReport = async ({displayRecords, devicesDetails, people, reportDetails, formats}) => {
	const allRecords = await getAllStaticReportRecords(reportDetails, devicesDetails);
	const sourceRecords = Object.keys(allRecords).length > 0 ? allRecords : displayRecords;

	const records = (Object.values(sourceRecords)).reduce<Record<string, typeof sourceRecords[keyof typeof sourceRecords][]>>(
		(acc, obj) => {
			const deviceId = obj.hasOwnProperty('device') ? obj['device'] : '';

			if (deviceId === '') return acc;

			if (!acc[deviceId]) {
				acc[deviceId] = [];
			}

			acc[deviceId].push(obj);

			return acc;
		},
		{}
	);

	if (formats.exportPdf) {
		exportReportPdf({records, report: reportDetails, reportData: devicesDetails, people});
	}

	if (formats.exportXlsx) {
		exportReportXlsx({records, report: reportDetails, reportData: devicesDetails, people});
	}

	if (formats.exportCsv) {
 		exportReport({displayRecords: sourceRecords, reportType: 'static', devicesDetails, people})()
	}
}

export const exportActivityReport = async ({formats, displayRecords}) => {
	if (formats.exportPdf) {
		const data = [];
		const pdf = new jsPDF();
		const pageWidth = pdf.internal.pageSize.width;
		const margin = 20;
		const width = (pageWidth - margin) / 2;

		for (const record of displayRecords) {
			data.push({
				head: [['Device Name', 'Last Report']],
				body: [[record.deviceName, record.ago]],
				columnStyles: {
					0: {cellWidth: width},
					1: {cellWidth: width}
				}
			})
		}

		data.forEach(table => {
			autoTable(pdf, table);
		});

		pdf.save('Report.pdf');
	}

	if (formats.exportXlsx) {
		const data = displayRecords.map(record => {
			return {
				'Device Name': record.deviceName,
				'Last Report': record.ago
			}
		});

		const worksheet = utils.json_to_sheet(data);
		const workbook = utils.book_new();

		utils.book_append_sheet(workbook, worksheet, "report");
		writeFileXLSX(workbook, "Report.xlsx");
	}

	if (formats.exportCsv) {
		const data = displayRecords.map(record => {
			return {
				'Device Name': record.deviceName,
				'Last Report': record.ago
			}
		});

		csvExporter('Report.csv')([['Device Name', 'Last Report'], ...data.map(item => Object.values(item))]);
	}
}

export const exportMileageReport = async ({formats, devices, startTime, endTime}) => {
	const URL = '/api/location/get-miles-per-state';
	const clientId = localStorage.get('active-client');
	const body = {
		"organization_key": clientId,
		"start_time": startTime,
		"end_time": endTime,
		"device_ids": devices
	}

	try {
		const {data} = await instance.post(URL, {...body});
		const reportData: iMileageReportData[] = Object.values(data);

		if (formats.exportPdf) {
			const pdf = new jsPDF();
			const pageWidth = pdf.internal.pageSize.width;
			const margin = 20;
			const width = (pageWidth - margin) / 2;
			const data = [];

			for (const entry of reportData) {
				const report = {
					head: [[entry.deviceName, '']],
					body: entry.trips.map(value => {
						return [value.state, value.mil]
					}),
					columnStyles: {
						1: { halign: 'right' }
					}
				}

				data.push(report);
			}

			data.forEach(table => {
				autoTable(pdf, table);
			});

			pdf.save(`mileage_report_${new Date().getTime()}.pdf`);
		}
		if (formats.exportXlsx) {
			const data = reportData.map((report: iMileageReportData) => {
				const values = report.trips.length > 0 ? report.trips.map((value) => {
					return {
						'Vehicles': report.deviceName,
						'State': value.state,
						'Mileage': value.mil
					}
				}) : [{
					'Vehicles': report.deviceName,
					'State': '',
					'Mileage': ''
				}]

				return values;
			})

			const formattedData = data.flat();
			const startMoment = typeof startTime === 'number'
				? (startTime > 1e12 ? moment(startTime) : moment.unix(startTime))
				: moment(startTime);
			const endMoment = typeof endTime === 'number'
				? (endTime > 1e12 ? moment(endTime) : moment.unix(endTime))
				: moment(endTime);
			const dateRange = `Search Date Range: ${startMoment.format('MM/DD/YYYY h:mm A')} - ${endMoment.format('MM/DD/YYYY h:mm A')}`;
			const worksheet = utils.aoa_to_sheet([[dateRange]]);
			utils.sheet_add_json(worksheet, formattedData, { origin: 'A2' });
			worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
			const workbook = utils.book_new();
			utils.book_append_sheet(workbook, worksheet, "report");
			writeFileXLSX(workbook, `mileage_report_${new Date().getTime()}.xlsx`);
		}
		if (formats.exportCsv) {
			const formattedData = [];

			if (reportData.length > 0) {
				for (const report of reportData) {
					if (report.trips.length > 0) {
						for (const value of report.trips) {
							formattedData.push([report.deviceName, value.state, value.mil]);
						}
					}
					else {
						formattedData.push([report.deviceName, '', '']);
					}
				}
			}

			csvExporter(`mileage_report_${new Date().getTime()}.csv`)([['Device Name', 'State', 'Mileage'], ...formattedData]);
		}
	} catch (e) {
		console.log(e)
	}
}
